import { useState } from 'react';
import { X } from 'lucide-react';
import { OroLoader } from './OroLoader';
import ConfirmDialog from './ConfirmDialog';

interface AvatarRemoveButtonProps {
  onRemove: () => void;
  isLoading?: boolean;
}

/**
 * AvatarRemoveButton - Red X button for removing profile avatars
 *
 * Positioned absolute top-left of avatar image (opposite of camera icon which is bottom-right).
 * Only render this when an avatar exists.
 *
 * Usage:
 * ```tsx
 * {profile?.profile_image_url && (
 *   <AvatarRemoveButton
 *     onRemove={handleRemovePhoto}
 *     isLoading={isRemoving}
 *   />
 * )}
 * ```
 */
export function AvatarRemoveButton({
  onRemove,
  isLoading = false
}: AvatarRemoveButtonProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setIsProcessing(true);
    try {
      onRemove();
      // Mutation state is managed via isLoading prop, no need to await
    } catch (error) {
      console.error('Failed to remove avatar:', error);
      // Error feedback is handled by parent component's hooks
    } finally {
      setIsProcessing(false);
    }
  };

  const isDisabled = isLoading || isProcessing;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        aria-label="Remove profile photo"
        className={`
          absolute -top-1 -left-1
          w-6 h-6 rounded-full
          bg-oro-error hover:bg-oro-error/90
          text-white
          flex items-center justify-center
          shadow-lg
          transition-all duration-200
          ${isDisabled ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
          border border-oro-error
        `}
      >
        {isDisabled ? (
          <OroLoader size={16} label="Removing" />
        ) : (
          <X className="w-3.5 h-3.5" />
        )}
      </button>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Remove Photo"
        message="Remove profile photo? You can upload a new one anytime."
        confirmLabel="Remove"
        cancelLabel="Keep"
        variant="destructive"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
}

export default AvatarRemoveButton;
